import { type RPPDomain } from '@retailer-platform/dashboard/core'
import { NavEntryPoint, AdminNavEntryPointV2 } from '@retailer-platform/dashboard/routing'
import { storefrontRouter } from './routing/storefrontRouter'
import {
  marketplaceOnlyPageBuilderViewAccessControl,
  pageBuilderViewAccessControl,
  storeConfigurationDraftViewAccessControl,
  slotManagerViewAccessControl,
  storefrontHomepageDashboardV2,
} from './access-control/storefrontAccess.configuration'
import { type DomainMessages } from './utils/domain/intl'
import { routes } from './routing/routes'
import EN_US from './translations/en-US.json'
import { type DomainAccessControlConfig } from './utils/domain/accessControl'
import { autosuggestViewAccessControl } from './access-control/autosuggestAccess.configuration'
import {
  departmentNavigationAccessControl,
  navigationViewAccessControl,
} from './access-control/navigationAccess.configuration'
import { navigationAdminViewAccessControl } from './access-control/storefrontAdminAccess.configuration'
import { storefrontAdminRouter } from './routing/storefrontAdminRouter'
import { storefrontPartnerScopedRouter } from './routing/storefrontPartnerScopedRouter'
import { creativeManagerV2AccessControl } from './access-control/creativeManagerAccess.configuration'
import { Permission } from './access-control/permissions'

const definition: RPPDomain<typeof routes, DomainMessages, DomainAccessControlConfig> = {
  id: 'storefront',
  totem: {
    // The default IPP tool of all routes in this domain - should match the Navigation Entry in IPP UI.
    // Individual routes can override this by specifying a `tool` prop on their DomainRoute component
    tool: 'storefront-dashboard-tool',
  },
  routes,
  translations: { EN_US },
  lazyTranslations: {
    ES_ES: () => import('./translations/es-ES.json'),
    FR_FR: () => import('./translations/fr-FR.json'),
  },
  routers: {
    admin: [storefrontAdminRouter],
    scoped: [storefrontRouter],
    partnerScoped: [storefrontPartnerScopedRouter],
  },
  navigationEntries: [
    {
      attachToV2: NavEntryPoint.Marketing,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'Configure promotional slot placements for featured products and hero banners on storefront',
          labelId: 'storefrontDomain.slotManager.pageTitle',
          route: 'storefront-slots-manager',
          subRoutes: ['storefront-slots-manager-detail'],
          accessControl: slotManagerViewAccessControl,
          position: 10,
          positionNavV2: 10,
          inToolPermissions: [
            {
              slug: Permission.StorefrontRetailerFundedMarketingModify,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.StorefrontRetailerFundedMarketingModify,
            },
          ],
        },
        {
          type: 'item',
          llmDescription:
            'Create and manage promotional creative assets and visual content for campaigns',
          labelId: 'storefrontDomain.creativeManagerV2.nav',
          route: 'storefront-creative-manager-v2',
          accessControl: creativeManagerV2AccessControl,
          position: 8,
          positionNavV2: 3,
          inToolPermissions: [
            {
              slug: Permission.CreativeManagerModify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.CreativeManagerModify,
            },
          ],
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.Merchandising,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'Configure brand appearance including colors, logos, and custom images for storefront',
          labelId: 'storefrontDomain.brand-settings.page-title',
          route: 'storefront-brand-settings',
          subRoutes: [
            'storefront-custom-images',
            'storefront-banner-logo',
            'storefront-brand-settings-edit',
            'storefront-brand-settings-v2',
          ],
          accessControl: storeConfigurationDraftViewAccessControl,
          position: 2,
          positionNavV2: 4,
          inToolPermissions: [
            {
              slug: Permission.StoreConfigurationDraftModify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.StoreConfigurationDraftModify,
            },
            {
              slug: Permission.StoreConfigsModify,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.StoreConfigsModify,
            },
          ],
        },
        {
          type: 'item',
          llmDescription:
            'Create and manage custom informational pages (legal policies, events, about us) on your storefront',
          labelId: 'storefrontDomain.contentPages.dashboard.pageTitle',
          route: 'storefront-content-pages-dashboard',
          subRoutes: ['storefront-content-pages-create'],
          accessControl: pageBuilderViewAccessControl,
          position: 3,
          positionNavV2: 5,
          inToolPermissions: [
            {
              slug: Permission.StorefrontPageBuilderModify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.StorefrontPageBuilderModify,
            },
          ],
        },
        {
          type: 'item',
          llmDescription: 'Manage homepage layout and featured content',
          labelId: 'storefrontDomain.homepage.dashboardV2.pageTitle',
          route: 'storefront-homepage-dashboard-v2',
          subRoutes: [
            'storefront-content-pages-edit',
            'storefront-homepage-dashboard',
            'storefront-homepage-edit',
          ],
          accessControl: storefrontHomepageDashboardV2,
          position: 4,
          positionNavV2: 6,
        },
        {
          type: 'item',
          llmDescription:
            'Configure search autosuggest suggestions and quick navigation shortcuts for storefront search bar',
          labelId: 'storefrontDomain.autosuggest-links-list.page-title',
          route: 'storefront-autosuggest-links-list',
          subRoutes: ['storefront-autosuggest-links-upsert', 'storefront-autosuggest-links-view'],
          accessControl: autosuggestViewAccessControl,
          position: 6,
          positionNavV2: 7,
          inToolPermissions: [
            {
              slug: Permission.AutosuggestModify,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.AutosuggestModify,
            },
          ],
        },
        {
          type: 'item',
          llmDescription: 'Create and manage custom pages for marketplace',
          labelId: 'storefrontDomain.contentPages.dashboard.pageTitle.marketplace',
          route: 'storefront-content-pages-dashboard',
          accessControl: marketplaceOnlyPageBuilderViewAccessControl,
          position: 7,
          positionNavV2: 8,
          inToolPermissions: [
            {
              slug: Permission.StoreConfigsView,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.StoreConfigsView,
            },
            {
              slug: Permission.StorefrontPageBuilderView,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.StorefrontPageBuilderView,
            },
            {
              slug: Permission.StoreConfigsModify,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.StoreConfigsModify,
            },
            {
              slug: Permission.StorefrontPageBuilderModify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.StorefrontPageBuilderModify,
            },
            {
              slug: Permission.ReadOnly,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.ReadOnly,
            },
            {
              slug: Permission.StoreConfigurationDraftView,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.StoreConfigurationDraftView,
            },
            {
              slug: Permission.StoreConfigurationDraftModify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.StoreConfigurationDraftModify,
            },
            {
              slug: Permission.StorefrontRetailerFundedMarketingView,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.StorefrontRetailerFundedMarketingView,
            },
            {
              slug: Permission.AdminPanel,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.AdminPanel,
            },
            {
              slug: Permission.StorefrontRetailerFundedMarketingModify,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.StorefrontRetailerFundedMarketingModify,
            },
          ],
        },
        {
          type: 'item',
          llmDescription:
            'Configure which product categories and collections appear in your storefront navigation menus',
          labelId: 'storefrontDomain.navigation.collections.title',
          route: 'storefront-navigation-collections',
          accessControl: departmentNavigationAccessControl,
          position: 1,
          positionNavV2: 1,
          inToolPermissions: [
            {
              slug: Permission.DepartmentsModify,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.DepartmentsModify,
            },
          ],
        },
        {
          type: 'item',
          llmDescription: 'Manage legacy navigation header configuration',
          labelId: 'storefrontDomain.navigation.navigation.legacy.title',
          route: 'storefront-navigation-header',
          accessControl: navigationViewAccessControl,
          positionNavV2: 3,
          inToolPermissions: [
            {
              slug: Permission.CollectionsView,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.CollectionsView,
            },
          ],
        },
      ],
    },
  ],
  adminNavigationEntries: [
    {
      attachToV2: AdminNavEntryPointV2.Merchandising,
      subNavItems: [
        {
          route: 'storefront-admin-navigation-footer',
          labelId: 'storefrontDomain.navigation.footer.mp.nav.title',
          accessControl: navigationAdminViewAccessControl,
          positionNavV2: 0,
        },
      ],
    },
  ],
}

export default definition
